import { useState, useEffect, useRef } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { Space, Select, Input, Flex, Modal, Popover, Button } from "antd";

import {
  FormOutlined,
  DeleteOutlined,
  DownOutlined,
  UpOutlined,
  CopyOutlined,
  MinusCircleOutlined,
  ClearOutlined,
} from "@ant-design/icons";

import { v4 as uuidv4 } from "uuid";

import "./styles.scss";
import { ActionButton } from "../../../components/elements/Button";
import { PlusCircle } from "react-bootstrap-icons";

const generateUniqueId = () => uuidv4();

export const InfoCircle = () => (
  <span className="custom-info-circle" aria-label="info">
    i
  </span>
);

const defaultStepCellStyle = {
  position: "relative",
  padding: "1rem",
  backgroundColor: "#fff",
  border: "1px solid rgba(219, 241, 255, 1)",
  borderRadius: "3px",
  cursor: "grab",
};

const stepTypes = {
  "": "Select action type",
  click_on_element: "Click on element",
  collect_network_event: "Collect network event",
  check_network_event_contains: "Check network event contains",
  verify_collected_events: "Verify collected events",
  go_to_page: "Go to page",
};

const stepParamDescriptions = {
  action_type: "Define an action type that has to be executed on the page.",
  delay: "A time delay after action complete (in seconds).",
  optional:
    "Whether current step is optional and should not interrupt the execution.",
  wait_for_page_load: "Wait for a page load after completing the action.",
  opens_new_page: "Move execution to the new page opened by the action.",
  element_selector: "Query selector of the element.",
  go_to_page: "The URL is the link of the page that you want to visit.",
  network_filters:
    "Filter network requests by key-value pairs (e.g. event: page, type: track).",
  network_check_value:
    "Value to search for in all previously collected network events.",
  network_template:
    "Define the expected payload structure for the network event.",
  network_metadata_checks:
    "Define assertions on network metadata fields such as response status, headers, method, and response body.",
  verify_events_mode:
    "Choose whether to verify that specific events were collected, or that no events were collected at all.",
  verify_events_filters:
    "Filter which collected events to check by key-value pairs (e.g. event: page).",
};

const metadataFieldOptions = [
  {
    label: "General",
    options: [
      { label: "Response Status", value: "responseStatus" },
      { label: "Method", value: "method" },
      { label: "Resource Type", value: "resourceType" },
      { label: "Request URL", value: "requestUrl" },
      { label: "Authority", value: "authority" },
      { label: "Path", value: "path" },
    ],
  },
  {
    label: "Request Headers",
    options: [
      { label: "Content-Type", value: "requestHeaders.content-type" },
      { label: "Referer", value: "requestHeaders.referer" },
      { label: "User-Agent", value: "requestHeaders.user-agent" },
      { label: "Sec-Ch-Ua", value: "requestHeaders.sec-ch-ua" },
      { label: "Other Request Header", value: "requestHeaders.*" },
    ],
  },
  {
    label: "Response",
    options: [
      { label: "Content-Length", value: "responseHeaders.content-length" },
      { label: "Other Response Header", value: "responseHeaders.*" },
      { label: "Response Body", value: "responseBody" },
    ],
  },
];

const metadataOperatorOptions = [
  { label: "equals", value: "equals" },
  { label: "not equals", value: "not_equals" },
  { label: "contains", value: "contains" },
  { label: "not contains", value: "not_contains" },
  { label: "greater than (>)", value: "greater_than" },
  { label: "less than (<)", value: "less_than" },
  { label: "greater or equal (>=)", value: "greater_or_equal" },
  { label: "less or equal (<=)", value: "less_or_equal" },
  { label: "matches regex", value: "matches_regex" },
  { label: "exists", value: "exists" },
  { label: "not empty", value: "not_empty" },
];

const verifyEventsCheckModes = [
  { label: "Expect matching events", value: "expect_events" },
  { label: "Expect no events", value: "expect_no_events" },
];

const defaultStepObject = () => ({
  step: null,
  stepId: generateUniqueId(),
  delay: 0,
  type: null,
  selector: null,
  optional: false,
  waitForNavigation: false,
  opensNewPage: false,
  value: null,
  networkFilters: [{ key: "", value: "" }],
  networkCheckValue: "",
  dataLayerObjToFind: null,
  networkMetadataChecks: [{ field: "", headerKey: "", operator: "equals", value: "" }],
  verifyEventsMode: "expect_events",
  verifyEventsFilters: [{ key: "", value: "" }],
});

// Sortable item component
function SortableItem(props) {
  const {
    step = {},
    index = 0,
    isCollapsed,
    handleDataLayerObjEdit = () => {},
    handleStepEdit = () => {},
    handleDeleteStepClick = () => {},
    handleCopyStepClick = () => {},
    handleToggleStepClick = () => {},
  } = props;

  const [stepId, setStepId] = useState(step?.stepId || "");
  const [stepType, setStepType] = useState(step?.type || "");
  const [stepSelector, setStepSelector] = useState(step?.selector || "");
  const [stepDelay, setStepDelay] = useState(step?.delay || 0);
  const [stepOptional, setStepOptional] = useState(!!step?.optional);
  const [stepWaitForPageLoad, setWaitForPageLoad] = useState(
    !!step?.waitForNavigation,
  );
  const [stepOpensNewPage, setStepOpensNewPage] = useState(
    !!step?.opensNewPage,
  );
  const [stepValue, setStepValue] = useState(step?.value);
  const [stepNetworkFilters, setStepNetworkFilters] = useState(
    step?.networkFilters || [],
  );
  const [stepNetworkCheckValue, setStepNetworkCheckValue] = useState(
    step?.networkCheckValue || "",
  );
  const [stepDataLayerObj, setStepDataLayerObj] = useState(
    step?.dataLayerObjToFind || [],
  );
  const [stepNetworkMetadataChecks, setStepNetworkMetadataChecks] = useState(
    step?.networkMetadataChecks?.length
      ? step.networkMetadataChecks
      : [{ field: "", headerKey: "", operator: "equals", value: "" }],
  );
  const [stepVerifyEventsMode, setStepVerifyEventsMode] = useState(
    step?.verifyEventsMode || "expect_events",
  );
  const [stepVerifyEventsFilters, setStepVerifyEventsFilters] = useState(
    step?.verifyEventsFilters || [{ key: "", value: "" }],
  );

  // useSortable hook
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: step.stepId,
      key: step.stepId,
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || undefined,
    width: "100%",
    ...defaultStepCellStyle,
  };

  // Skip edit propagation on mount and after syncing from parent prop
  const skipEditRef = useRef(true);
  // Track whether the parent update was caused by our own propagation (echo)
  const isLocalUpdateRef = useRef(false);
  // Track whether SortableItem has completed its initial mount sync
  const isMountedRef = useRef(false);

  useEffect(() => {
    if (skipEditRef.current) {
      skipEditRef.current = false;
      return;
    }
    const newStepData = {
      ...step,
      step: index,
      type: stepType,
      selector: stepSelector,
      delay: stepDelay,
      optional: stepOptional,
      waitForNavigation: stepWaitForPageLoad,
      opensNewPage: stepOpensNewPage,
      value: stepValue,
      networkFilters: stepNetworkFilters,
      networkCheckValue: stepNetworkCheckValue,
      dataLayerObjToFind: stepDataLayerObj,
      networkMetadataChecks: stepNetworkMetadataChecks,
      verifyEventsMode: stepVerifyEventsMode,
      verifyEventsFilters: stepVerifyEventsFilters,
    };

    isLocalUpdateRef.current = true;
    handleStepEdit(stepId, newStepData);
  }, [
    stepType,
    stepSelector,
    stepDelay,
    stepOptional,
    stepWaitForPageLoad,
    stepOpensNewPage,
    stepValue,
    stepNetworkFilters,
    stepNetworkCheckValue,
    stepDataLayerObj,
    stepNetworkMetadataChecks,
    stepVerifyEventsMode,
    stepVerifyEventsFilters,
  ]);

  useEffect(() => {
    // On initial mount: sync state but do NOT re-arm skipEditRef.
    // Re-arming on mount is what caused the first user keystroke to be dropped.
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      setStepId(step?.stepId);
      setStepType(step?.type);
      setStepSelector(step?.selector);
      setStepDelay(step?.delay);
      setStepOptional(step?.optional);
      setWaitForPageLoad(step?.waitForNavigation);
      setStepOpensNewPage(step?.opensNewPage);
      setStepValue(step?.value);
      setStepNetworkFilters(step?.networkFilters || []);
      setStepNetworkCheckValue(step?.networkCheckValue || "");
      setStepDataLayerObj(step?.dataLayerObjToFind || []);
      setStepNetworkMetadataChecks(
        step?.networkMetadataChecks?.length
          ? step.networkMetadataChecks
          : [{ field: "", headerKey: "", operator: "equals", value: "" }],
      );
      setStepVerifyEventsMode(step?.verifyEventsMode || "expect_events");
      setStepVerifyEventsFilters(step?.verifyEventsFilters || [{ key: "", value: "" }]);
      return;
    }
    // Skip sync when the prop change is just our own edit echoing back
    if (isLocalUpdateRef.current) {
      isLocalUpdateRef.current = false;
      return;
    }
    // Genuine external prop change: re-arm skip and sync state
    skipEditRef.current = true;
    setStepId(step?.stepId);
    setStepType(step?.type);
    setStepSelector(step?.selector);
    setStepDelay(step?.delay);
    setStepOptional(step?.optional);
    setWaitForPageLoad(step?.waitForNavigation);
    setStepOpensNewPage(step?.opensNewPage);
    setStepValue(step?.value);
    setStepNetworkFilters(step?.networkFilters || []);
    setStepNetworkCheckValue(step?.networkCheckValue || "");
    setStepDataLayerObj(step?.dataLayerObjToFind || []);
    setStepNetworkMetadataChecks(
      step?.networkMetadataChecks?.length
        ? step.networkMetadataChecks
        : [{ field: "", headerKey: "", operator: "equals", value: "" }],
    );
    setStepVerifyEventsMode(step?.verifyEventsMode || "expect_events");
    setStepVerifyEventsFilters(step?.verifyEventsFilters || [{ key: "", value: "" }]);
  }, [step]);

  // Network filter handlers
  const handleAddFilter = () => {
    setStepNetworkFilters([...stepNetworkFilters, { key: "", value: "" }]);
  };

  const handleRemoveFilter = (idx) => {
    setStepNetworkFilters(stepNetworkFilters.filter((_, i) => i !== idx));
  };

  const handleFilterChange = (idx, field, val) => {
    const updated = stepNetworkFilters.map((f, i) =>
      i === idx ? { ...f, [field]: val } : f,
    );
    setStepNetworkFilters(updated);
  };

  // Network metadata check handlers
  const handleAddMetadataCheck = () => {
    setStepNetworkMetadataChecks([
      ...stepNetworkMetadataChecks,
      { field: "", headerKey: "", operator: "equals", value: "" },
    ]);
  };

  const handleRemoveMetadataCheck = (idx) => {
    setStepNetworkMetadataChecks(
      stepNetworkMetadataChecks.filter((_, i) => i !== idx),
    );
  };

  const handleMetadataCheckChange = (idx, key, val) => {
    const updated = stepNetworkMetadataChecks.map((check, i) =>
      i === idx ? { ...check, [key]: val } : check,
    );
    setStepNetworkMetadataChecks(updated);
  };

  // Verify events filter handlers
  const handleAddVerifyFilter = () => {
    setStepVerifyEventsFilters([...stepVerifyEventsFilters, { key: "", value: "" }]);
  };

  const handleRemoveVerifyFilter = (idx) => {
    setStepVerifyEventsFilters(
      stepVerifyEventsFilters.filter((_, i) => i !== idx),
    );
  };

  const handleVerifyFilterChange = (idx, field, val) => {
    const updated = stepVerifyEventsFilters.map((filter, i) =>
      i === idx ? { ...filter, [field]: val } : filter,
    );
    setStepVerifyEventsFilters(updated);
  };

  return (
    <Flex align="start" style={{ width: "100%" }} gap={38}>
      {/* Step number badge */}
      <div className="scenario-step-number">{index}</div>

      {/* The card */}
      <div
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        style={{ ...style, flexDirection: "column", width: "100%" }}
      >
        {/* Top-right icon row */}
        <Space
          size={8}
          style={{
            position: "absolute",
            top: "1rem",
            right: "1rem",
            zIndex: 2,
          }}
        >
          <CopyOutlined
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => handleCopyStepClick(step.stepId)}
            style={{ cursor: "pointer" }}
          />
          <DeleteOutlined
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => handleDeleteStepClick(step.stepId)}
            style={{ cursor: "pointer" }}
          />
          {isCollapsed ? (
            <DownOutlined
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => handleToggleStepClick(step.stepId)}
              style={{ cursor: "pointer" }}
            />
          ) : (
            <UpOutlined
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => handleToggleStepClick(step.stepId)}
              style={{ cursor: "pointer" }}
            />
          )}
        </Space>

        {/* Main form fields */}
        {isCollapsed && (
          <div>{stepType ? stepTypes[stepType] : "No action selected…"}</div>
        )}
        {!isCollapsed && (
          <Space direction="vertical" size={20} style={{ width: "100%" }}>
            {/* Type of action */}
            <Popover
              placement="topLeft"
              content={stepParamDescriptions.action_type}
            >
              <Space size={5} align="center">
                <label className="scenario-step-label">
                  Type of (inter) action
                </label>
                <InfoCircle />
              </Space>
            </Popover>
            <Select
              className="scenario-step-select"
              options={Object.keys(stepTypes).map((key) => ({
                value: key,
                label: stepTypes[key],
                disabled: key === "",
              }))}
              value={stepType}
              placeholder="Select action type"
              onChange={(value) => setStepType(value)}
              popupMatchSelectWidth={false}
              onPointerDown={(e) => e.stopPropagation()}
              style={{ height: 30 }}
            />

            {/* Element selector (for click_on_element) */}
            {stepType === "click_on_element" && (
              <Space size={5} direction="vertical">
                <Popover
                  placement="topLeft"
                  content={stepParamDescriptions.element_selector}
                >
                  <Space size={5} align="center">
                    <label className="scenario-step-label">
                      Element selector
                    </label>
                    <InfoCircle />
                  </Space>
                </Popover>
                <Input
                  name="step_selector"
                  className="scenario-step-select"
                  value={stepSelector}
                  placeholder="Element selector"
                  onChange={(e) => setStepSelector(e.target.value)}
                  onBlur={(e) => { if (e.target.value !== stepSelector) setStepSelector(e.target.value); }}
                  onPointerDown={(e) => e.stopPropagation()}
                  style={{ minWidth: 220, height: 30 }}
                />
              </Space>
            )}

            {/* Go to page URL input */}
            {stepType === "go_to_page" && (
              <Space size={5} direction="vertical">
                <Popover
                  placement="topLeft"
                  content={stepParamDescriptions.go_to_page}
                >
                  <Space size={5} align="center">
                    <label className="scenario-step-label">Page URL</label>
                    <InfoCircle />
                  </Space>
                </Popover>
                <Input
                  className="scenario-step-select"
                  name="step_value"
                  value={stepValue}
                  placeholder="URL"
                  onChange={(e) => setStepValue(e.target.value)}
                  onBlur={(e) => { if (e.target.value !== stepValue) setStepValue(e.target.value); }}
                  onPointerDown={(e) => e.stopPropagation()}
                  style={{ minWidth: 220, height: 30 }}
                />
              </Space>
            )}

            {/* Collect network event - dynamic filters + template editor */}
            {stepType === "collect_network_event" && (
              <Space size={10} direction="vertical" style={{ width: "100%" }}>
                <Popover
                  placement="topLeft"
                  content={stepParamDescriptions.network_filters}
                >
                  <Space size={5} align="center">
                    <label className="scenario-step-label">
                      Network request filters
                    </label>
                    <InfoCircle />
                    <ClearOutlined
                      title="Clear all filters"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={() => setStepNetworkFilters([{ key: "", value: "" }])}
                      style={{ cursor: "pointer", color: "#999", fontSize: 12 }}
                    />
                  </Space>
                </Popover>

                {stepNetworkFilters.map((filter, idx) => (
                  <div key={`filter-${idx}`} className="network-filter-row">
                    <Input
                      placeholder="Key (e.g. event)"
                      value={filter.key}
                      onChange={(e) =>
                        handleFilterChange(idx, "key", e.target.value)
                      }
                      onBlur={(e) => { if (e.target.value !== filter.key) handleFilterChange(idx, "key", e.target.value); }}
                      onPointerDown={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      style={{ width: 140, height: 30 }}
                    />
                    <Input
                      placeholder="Value (e.g. page)"
                      value={filter.value}
                      onChange={(e) =>
                        handleFilterChange(idx, "value", e.target.value)
                      }
                      onBlur={(e) => { if (e.target.value !== filter.value) handleFilterChange(idx, "value", e.target.value); }}
                      onPointerDown={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      style={{ width: 180, height: 30 }}
                    />
                    {stepNetworkFilters.length > 1 && (
                      <MinusCircleOutlined
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={() => handleRemoveFilter(idx)}
                        style={{ cursor: "pointer" }}
                      />
                    )}
                    <PlusCircle
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={handleAddFilter}
                      style={{ cursor: "pointer" }}
                    />
                  </div>
                ))}

                {/* Network metadata checks */}
                <Popover
                  placement="topLeft"
                  content={stepParamDescriptions.network_metadata_checks}
                >
                  <Space size={5} align="center">
                    <label className="scenario-step-label">
                      Network metadata assertions
                    </label>
                    <InfoCircle />
                    <ClearOutlined
                      title="Clear all assertions"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={() => setStepNetworkMetadataChecks([{ field: "", headerKey: "", operator: "equals", value: "" }])}
                      style={{ cursor: "pointer", color: "#999", fontSize: 12 }}
                    />
                  </Space>
                </Popover>

                {stepNetworkMetadataChecks.map((check, idx) => {
                  const isHeaderField =
                    check.field === "requestHeaders.*" ||
                    check.field === "responseHeaders.*";
                  const noValueOperator =
                    check.operator === "exists" ||
                    check.operator === "not_empty";

                  return (
                    <div key={`meta-${idx}`} className="network-metadata-check-row">
                      <Select
                        placeholder="Field"
                        value={check.field || undefined}
                        options={metadataFieldOptions}
                        onChange={(val) =>
                          handleMetadataCheckChange(idx, "field", val)
                        }
                        onPointerDown={(e) => e.stopPropagation()}
                        popupMatchSelectWidth={false}
                        style={{ width: 160, height: 30 }}
                      />
                      {isHeaderField && (
                        <Input
                          placeholder="Header name"
                          value={check.headerKey || ""}
                          onChange={(e) =>
                            handleMetadataCheckChange(
                              idx,
                              "headerKey",
                              e.target.value,
                            )
                          }
                          onBlur={(e) => { if (e.target.value !== (check.headerKey || "")) handleMetadataCheckChange(idx, "headerKey", e.target.value); }}
                          onPointerDown={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                          style={{ width: 140, height: 30 }}
                        />
                      )}
                      <Select
                        placeholder="Operator"
                        value={check.operator || undefined}
                        options={metadataOperatorOptions}
                        onChange={(val) =>
                          handleMetadataCheckChange(idx, "operator", val)
                        }
                        onPointerDown={(e) => e.stopPropagation()}
                        popupMatchSelectWidth={false}
                        style={{ width: 140, height: 30 }}
                      />
                      {!noValueOperator && (
                        <Input
                          placeholder="Expected value"
                          value={check.value || ""}
                          onChange={(e) =>
                            handleMetadataCheckChange(
                              idx,
                              "value",
                              e.target.value,
                            )
                          }
                          onBlur={(e) => { if (e.target.value !== (check.value || "")) handleMetadataCheckChange(idx, "value", e.target.value); }}
                          onPointerDown={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                          style={{ width: 180, height: 30 }}
                        />
                      )}
                      {stepNetworkMetadataChecks.length > 1 && (
                        <MinusCircleOutlined
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={() => handleRemoveMetadataCheck(idx)}
                          style={{ cursor: "pointer" }}
                        />
                      )}
                      <PlusCircle
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={handleAddMetadataCheck}
                        style={{ cursor: "pointer" }}
                      />
                    </div>
                  );
                })}

                <Popover
                  placement="topLeft"
                  content={stepParamDescriptions.network_template}
                >
                  <Space size={5} align="center">
                    <label className="scenario-step-label">
                      Expected payload template
                    </label>
                    <InfoCircle />
                  </Space>
                </Popover>
                <ActionButton
                  type="primary"
                  text={
                    <Space size={10}>
                      <FormOutlined /> Edit object template
                    </Space>
                  }
                  height={30}
                  width={200}
                  onClickFunction={() =>
                    handleDataLayerObjEdit(stepDataLayerObj, stepId)
                  }
                />
              </Space>
            )}
            {/* Check network event contains - single value input */}
            {stepType === "check_network_event_contains" && (
              <Space size={5} direction="vertical">
                <Popover
                  placement="topLeft"
                  content={stepParamDescriptions.network_check_value}
                >
                  <Space size={5} align="center">
                    <label className="scenario-step-label">
                      Value to check for
                    </label>
                    <InfoCircle />
                  </Space>
                </Popover>
                <Input
                  name="step_network_check_value"
                  className="scenario-step-select"
                  value={stepNetworkCheckValue}
                  placeholder="Search value in collected network events"
                  onChange={(e) => setStepNetworkCheckValue(e.target.value)}
                  onBlur={(e) => { if (e.target.value !== stepNetworkCheckValue) setStepNetworkCheckValue(e.target.value); }}
                  onPointerDown={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  style={{ minWidth: 280, height: 30 }}
                />
              </Space>
            )}
            {/* Verify collected events */}
            {stepType === "verify_collected_events" && (
              <Space size={10} direction="vertical" style={{ width: "100%" }}>
                <Popover
                  placement="topLeft"
                  content={stepParamDescriptions.verify_events_mode}
                >
                  <Space size={5} align="center">
                    <label className="scenario-step-label">
                      Verification mode
                    </label>
                    <InfoCircle />
                  </Space>
                </Popover>
                <Select
                  value={stepVerifyEventsMode}
                  options={verifyEventsCheckModes}
                  onChange={(val) => setStepVerifyEventsMode(val)}
                  onPointerDown={(e) => e.stopPropagation()}
                  popupMatchSelectWidth={false}
                  style={{ width: 220, height: 30 }}
                />

                {stepVerifyEventsMode === "expect_events" && (
                  <>
                    <Popover
                      placement="topLeft"
                      content={stepParamDescriptions.verify_events_filters}
                    >
                      <Space size={5} align="center">
                        <label className="scenario-step-label">
                          Event filters
                        </label>
                        <InfoCircle />
                        <ClearOutlined
                          title="Clear all filters"
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={() => setStepVerifyEventsFilters([{ key: "", value: "" }])}
                          style={{ cursor: "pointer", color: "#999", fontSize: 12 }}
                        />
                      </Space>
                    </Popover>
                    {stepVerifyEventsFilters.map((filter, idx) => (
                      <div key={`vf-${idx}`} className="network-filter-row">
                        <Input
                          placeholder="Key (e.g. event)"
                          value={filter.key}
                          onChange={(e) =>
                            handleVerifyFilterChange(idx, "key", e.target.value)
                          }
                          onBlur={(e) => { if (e.target.value !== filter.key) handleVerifyFilterChange(idx, "key", e.target.value); }}
                          onPointerDown={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                          style={{ width: 140, height: 30 }}
                        />
                        <Input
                          placeholder="Value (e.g. page)"
                          value={filter.value}
                          onChange={(e) =>
                            handleVerifyFilterChange(idx, "value", e.target.value)
                          }
                          onBlur={(e) => { if (e.target.value !== filter.value) handleVerifyFilterChange(idx, "value", e.target.value); }}
                          onPointerDown={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                          style={{ width: 180, height: 30 }}
                        />
                        {stepVerifyEventsFilters.length > 1 && (
                          <MinusCircleOutlined
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={() => handleRemoveVerifyFilter(idx)}
                            style={{ cursor: "pointer" }}
                          />
                        )}
                        <PlusCircle
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={handleAddVerifyFilter}
                          style={{ cursor: "pointer" }}
                        />
                      </div>
                    ))}
                  </>
                )}
              </Space>
            )}
          </Space>
        )}
      </div>
    </Flex>
  );
}

// Sortable list component
const ensureStepIds = (steps) =>
  steps.map((step) =>
    step.stepId ? step : { ...step, stepId: generateUniqueId() },
  );

export const NetworkSortableList = (props) => {
  const {
    items = [],
    handleDataLayerObjEdit = () => {},
    handleStepsUpdate = () => {},
  } = props;

  const [curItems, setCurItems] = useState(() => ensureStepIds(items));
  const curItemsRef = useRef(curItems);

  // Helper to keep ref and state in sync
  const updateCurItems = (newItems) => {
    curItemsRef.current = newItems;
    setCurItems(newItems);
  };
  const [stepToDeleteId, setStepToDeleteId] = useState(null);
  const [collapsedMap, setCollapsedMap] = useState({});
  const [stepRemoveConfirmation, setStepRemoveConfirmation] = useState(false);

  const handleDeleteStepClick = (id) => {
    setStepRemoveConfirmation(true);
    setStepToDeleteId(id);
  };
  const handleDeleteStepConfirmation = () => {
    setStepRemoveConfirmation(false);
    handleStepDelete();
  };
  const handleDeleteStepCancel = () => {
    setStepRemoveConfirmation(false);
    setStepToDeleteId(null);
  };

  const handleCopyStepClick = (stepId) => {
    const prev = curItemsRef.current;
    const idx = prev.findIndex((s) => s.stepId === stepId);
    if (idx === -1) return;
    const original = prev[idx];
    const clone = { ...original, stepId: generateUniqueId() };
    const newItems = [...prev.slice(0, idx + 1), clone, ...prev.slice(idx + 1)];
    updateCurItems(newItems);
    handleStepsUpdate(newItems);
  };

  const handleToggleStepClick = (stepId) => {
    setCollapsedMap((prev) => ({ ...prev, [stepId]: !prev[stepId] }));
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active?.id && over?.id && active.id !== over.id) {
      const prevItems = curItemsRef.current;
      const oldIndex = prevItems.findIndex((i) => i.stepId === active.id);
      const newIndex = prevItems.findIndex((i) => i.stepId === over.id);
      const newOrder = arrayMove(prevItems, oldIndex, newIndex);
      updateCurItems(newOrder);
      handleStepsUpdate(newOrder);
    }
  };

  const handleStepAdd = () => {
    const newItems = [...curItemsRef.current, defaultStepObject()];
    updateCurItems(newItems);
    handleStepsUpdate(newItems);
  };

  const handleStepEdit = (id, step) => {
    const newItemsList = curItemsRef.current.map((elem) =>
      id === elem.stepId ? step : elem,
    );
    updateCurItems(newItemsList);
    handleStepsUpdate(newItemsList);
  };

  const handleStepDelete = () => {
    const newItemsList =
      curItemsRef.current.filter((elem) => elem.stepId !== stepToDeleteId) || [];
    updateCurItems(newItemsList);
    handleStepsUpdate(newItemsList);
  };

  useEffect(() => updateCurItems(ensureStepIds(items)), [items]);

  return (
    <div className="sortable-list-container">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext
          items={curItems.map((item) => item.stepId)}
          strategy={verticalListSortingStrategy}
        >
          <div style={{ display: "flex", flexDirection: "column" }}>
            {curItems.length
              ? curItems.map((step, idx) => (
                  <div
                    key={step.stepId}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      marginBottom: 24,
                      width: "100%",
                    }}
                  >
                    <SortableItem
                      key={step.stepId}
                      index={idx + 1}
                      step={step}
                      handleDataLayerObjEdit={handleDataLayerObjEdit}
                      handleStepEdit={handleStepEdit}
                      handleCopyStepClick={handleCopyStepClick}
                      handleDeleteStepClick={handleDeleteStepClick}
                      handleToggleStepClick={handleToggleStepClick}
                      isCollapsed={!!collapsedMap[step.stepId]}
                    />
                  </div>
                ))
              : null}
            <div style={{ marginTop: 16 }}>
              <Button type="default" onClick={handleStepAdd}>
                + add step
              </Button>
            </div>
          </div>
        </SortableContext>
      </DndContext>

      <Modal
        centered
        open={stepRemoveConfirmation}
        title="Confirm"
        onOk={() => handleDeleteStepConfirmation()}
        onCancel={() => handleDeleteStepCancel()}
        footer={(_, { OkBtn, CancelBtn }) => (
          <Flex gap={5}>
            <CancelBtn />
            <OkBtn />
          </Flex>
        )}
      >
        <p>Do you want to delete this step?</p>
      </Modal>
    </div>
  );
};
